<script setup>
    // Libraries
    import { storeToRefs } from 'pinia';
    const { signOut, isLoggedIn } = useAuth()
    // Stores
    import { useUserStore } from '@/stores/user';
    const userStore = useUserStore();

    // Composables
    import { updateHead } from '@/composables/helpers/updateHead';
    import { detailTitle, SEO_ORIGIN, SEO_SITE_NAME } from '@/utils/seo';
    // Variables
    const { $listen } = useNuxtApp();
    const selectedItinerary = ref(null);
    const route = useRoute();
    const router = useRouter();
    const shared = ref(false);

    const { itineraries: ohcItineraries } = storeToRefs(userStore);

    // Reopen whatever the URL names, so a reload mid-flow doesn't dump you back
    // on the list — Asana O2-54. See app/utils/itineraryUrl.ts for why the
    // itinerary has to live in the address at all.
    //
    // A watcher rather than a one-shot in onMounted: `ohcItineraries` is empty
    // until the store hydrates from localStorage, and for a logged-in user the
    // whole array is replaced again when the server sync returns. Either of
    // those lands after mount, so a single early attempt would find nothing and
    // silently give up. It re-runs until it matches, then stops looking.
    watch(
        [ohcItineraries, () => route.query.itinerary],
        () => {
            if (selectedItinerary.value || route.query.shared) return;
            const match = itineraryFromQuery(ohcItineraries.value, route.query.itinerary);
            if (match) selectedItinerary.value = match;
        },
        { immediate: true, deep: true },
    );

    // The server sync replaces `ohcItineraries` with new objects. The open
    // itinerary was one of the old ones, so from then on it was a detached
    // copy: Apply wrote to the store's object and the page went on rendering
    // the stale one (O2-54). Follow it into the new array by name.
    watch(ohcItineraries, (list) => {
        const open = selectedItinerary.value;
        if (!open || route.query.shared || !list?.length || list.includes(open)) return;
        const current = list.find((item) => item?.name === open.name);
        if (current) selectedItinerary.value = current;
    });

    // Opening and closing rewrite the query in place. `replace`, not `push`:
    // single-itinerary.vue already closes with router.replace, and pushing would
    // put every itinerary you glance at into the back stack.
    function rememberItineraryInUrl(name) {
        if (import.meta.server) return;
        router.replace({ query: withItinerary(route.query, name) });
    }

    const shareItinerary = (itinerary) => {
        if (ohcItineraries.value && ohcItineraries.value.length > 0) {
            const compare = ohcItineraries.value.filter((item) => {
                return item.name == itinerary.name && item.sites.length == itinerary.sites.length && item.sites.every((element, index) => element === itinerary.sites[index]);
            });
            if (compare.length) {
                return false;
            } else {
                return true;
            }
        } else {
            return true;
        }
    };

    onMounted(() => {
        if (import.meta.client) {
            if (route.query.shared) {
                const name = route.query.title;
                const sites = route.query.sites.split(',');
                // compare query params with what's in the list of itineraries
                shared.value = shareItinerary({
                    name: decodeURI(name),
                    sites: sites,
                    shared: true,
                });
                selectedItinerary.value = {
                    name: decodeURI(name),
                    sites: sites,
                    shared: shared.value,
                };
            }
            $listen('refreshItineraryList', () => {
                // let list = getItineraries();
                if (ohcItineraries.value) {
                    ohcItineraries.value.forEach((singleItinerary) => {
                        if (singleItinerary?.name === selectedItinerary?.value?.name) {
                            selectedItinerary.value = singleItinerary;
                        }
                    });
                }
            });

            $listen('itineraryDeleted', (val) => {
                deletedItineraryName.value = val;
                showDeleteAlert();
            });

            $listen('globalHeaderNavOpen', () => {
                closeDetail();
            });
        }
    });

    const newItineraryModalIsOpen = ref(false);
    function openItineraryModal() {
        newItineraryModalIsOpen.value = true;
        document.body.style.overflow = 'hidden';

        return newItineraryModalIsOpen;
    }

    function hideModalWithName(val) {
        newItineraryModalIsOpen.value = false;
        document.body.style.overflow = 'auto';
        // getItineraries();

        if (val?.value?.length) {
            showNewItineraryBanner(val.value);
        }
        return newItineraryModalIsOpen;
    }

    function hideModal() {
        newItineraryModalIsOpen.value = false;
        document.body.style.overflow = 'auto';
        // getItineraries();
        return newItineraryModalIsOpen;
    }

    const hasNewItinerary = ref(false);
    const newItineraryName = ref('');
    const deletedItinerary = ref(false);
    const deletedItineraryName = ref('');
    function showNewItineraryBanner(name) {
        newItineraryName.value = name;
        hasNewItinerary.value = true;
    }
    function closeAlert() {
        hasNewItinerary.value = false;
        newItineraryName.value = '';
    }
    function showDeleteAlert() {
        deletedItinerary.value = true;
    }
    function closeDeleteAlert() {
        deletedItinerary.value = false;
        deletedItineraryName.value = '';
    }

    function handleSelection(val) {
        selectedItinerary.value = val;
        rememberItineraryInUrl(val?.name);
    }

    function closeDetail() {
        selectedItinerary.value = null;
        rememberItineraryInUrl(null);
    }

    // og:url for a shared itinerary, in the same shape user-share-button.vue
    // shares: /my-ohc/itineraries?title=…&sites=…&shared=true. It used to be
    // assembled by hand as "…/?title=My Trip&sites=…&shared=true/" (unencoded,
    // a stray "/?" and a trailing slash), and the watcher tested the ref
    // `selectedItinerary` rather than its value, so it was always truthy and
    // emitted "title=undefined" after closing an itinerary.
    const sharedItineraryUrl = (title, sites) => {
        const params = new URLSearchParams({ title: String(title ?? ''), sites: String(sites ?? ''), shared: 'true' });
        return `${SEO_ORIGIN}/my-ohc/itineraries?${params}`;
    };

    updateHead(() => {
        const shared = selectedItinerary.value
            ? { title: selectedItinerary.value.name, sites: selectedItinerary.value.sites }
            : route.query.shared
              ? { title: route.query.title, sites: route.query.sites }
              : null;
        return {
            title: shared?.title ? detailTitle(shared.title) : `Itineraries | ${SEO_SITE_NAME}`,
            path: '/my-ohc/itineraries',
            url: shared ? sharedItineraryUrl(shared.title, shared.sites) : undefined,
            description: 'Check out this itinerary I made for Open House Chicago!',
            noindex: true,
        };
    });

    // The open itinerary is a position:fixed panel over this list, so the page
    // underneath kept scrolling: on a phone, scrolling an itinerary showed blank
    // white and then slivers of the footer at the bottom edge (Asana O2-71).
    // A class rather than body.style.overflow, which the app's modals each set
    // back to 'auto' on close and would release this lock behind the panel.
    if (import.meta.client) {
        watch(selectedItinerary, (open) => document.documentElement.classList.toggle('itinerary-open', !!open), { immediate: true });
        onBeforeUnmount(() => document.documentElement.classList.remove('itinerary-open'));
    }
</script>

<template>
    <div class="my-ohc-page">
        <BlocksMyOhcNav />
        <div class="my-itineraries-wrapper container hd-container">
            <section>
                <ul class="itinerary-list">
                    <li v-for="itinerary in ohcItineraries" :key="itinerary.name" class="item">
                        <CardsItinerary :itinerary="itinerary" @itinerarySelected="handleSelection" />
                    </li>
                    <li>
                        <button @click.prevent="openItineraryModal" class="create-button uppercase">Create A New Itinerary</button>
                    </li>
                    <li>
                        <p class="body-book app-text" v-if="!isLoggedIn">
                            <NuxtLink to="/login" class="login-link">Log in to My OHC</NuxtLink> <strong>before</strong> saving sites or itineraries to access them across devices. If you don't see your favorites, make sure you're logged in here and on the device where you first saved them.
                        </p>
                        <p class="body-book app-text" v-else>
                            <strong>You're logged in to My OHC</strong>. Any sites or itineraries you save here will sync across devices. If you don't see items saved elsewhere, be sure you were logged in on that device when you saved them. <a href="#" @click="signOut" class="turquoise-alert-copy">Log out</a>.
                        </p>                        
                    </li>
                </ul>
            </section>
            <!-- Create new Itinerary. -->
            <GlobalFlyout v-if="newItineraryModalIsOpen" @toggleFilters="hideModal">
                <div class="modal-inner-container">
                    <ItinerariesCreate @closeFlyout="hideModal" @closeFlyoutWithName="hideModalWithName" :itinerary="selectedItinerary ? selectedItinerary : null" />
                </div>
            </GlobalFlyout>
            <!-- View single Itinerary. -->
            <GlobalSingleItinerary
                @save="openItineraryModal"
                v-if="selectedItinerary !== null"
                :itinerary="selectedItinerary"
                @closeItineraryDetail="closeDetail"
                :shared="shared" />
            <ItinerariesCreatedAlert v-if="hasNewItinerary" @closeAlert="closeAlert" :name="newItineraryName" />
            <ItinerariesDeletedAlert v-if="deletedItinerary" @closeAlert="closeDeleteAlert" :name="deletedItineraryName" />
        </div>
    </div>
</template>

<style lang="scss" scoped>
    .my-ohc-page {
        max-width: $hd;
        margin: auto;
        min-height: 400px;
        padding-top: 16px;
                
        .itinerary-list {
            .item {
                margin-bottom: 15px;
                border: 1px solid $gray-common;
            }
        }

        .app-text strong.red {
            color: $red;
        }

        .login-link {
            color: $red;
            text-decoration: underline;
            
            &:hover {
                color: $turquoise;
            }
        }

        .create-button {
            width: 100%;
            margin-bottom: 20px;
            padding: 14px 12px 10px;
            background-color: $black;
            color: $white;
            font-family: $avenir;
            text-align: center;

            @media (min-width: $tablet) {
                width: auto;
                // padding: 14px 50px 10px;
                margin-top: 50px;
                border: 1px solid;
                border-radius: 3.333em;
                padding: 1em 2em;
                transition: 0.25s all;

                &:hover {
                    background-color: $white;
                    color: $black;
                }
            }
        }

        .modal-inner-container {
            height: calc(100svh - 198px);
            background-color: $white;

            @media (min-width: $tablet) {
                height: auto;
            }
        }

        @media (min-width: $tablet) {
            max-width: $hd;
            margin: auto;

            .itinerary-list {
                display: grid;
                grid-template-columns: repeat(4, 1fr);
                grid-gap: 10px;
                padding: 30px 0;
            }

            li:has(.create-button) {
                display: flex;
                justify-content: center;
                align-items: center;
                grid-column-start: 1;
                grid-column-end: 5;
            }

            li:has(.app-text) {
                display: flex;
                justify-content: center;
                align-items: center;
                grid-column-start: 1;
                grid-column-end: 5;
            }
        }
    }
</style>

<style lang="scss">
    html.itinerary-open,
    html.itinerary-open body {
        overflow: hidden;
    }
</style>
